/**
 * ProjectFormPage — Criação de projeto (página com ABAS, padrão AccountPage).
 *
 * Abas: Dados | Cronograma | Contatos & Notificações. O estado transiente vive
 * no `useProjectDraft`; ao salvar, `validateDraft` posiciona o usuário na aba
 * com erro, e `toCreateRequest` monta o payload composto (converte dependências
 * `localId → taskIndex`). Cada tarefa vira um ticket — o header mostra o custo
 * em créditos antes de confirmar.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Breadcrumb, Button, PageToolbar, Tabs } from '@/shared/components/ui';
import type { TabItem } from '@/shared/components/ui';

import { useCreateProject } from '../hooks';
import { useProjectDraft, type ProjectFormTab } from '../hooks/useProjectDraft';
import { DadosTab } from '../components/form/DadosTab';
import { CronogramaTab } from '../components/form/CronogramaTab';
import { ContatosNotificacoesTab } from '../components/form/ContatosNotificacoesTab';

export const ProjectFormPage: React.FC = () => {
  const navigate = useNavigate();
  const draft = useProjectDraft();
  const createMutation = useCreateProject();
  const [activeTab, setActiveTab] = useState<ProjectFormTab>('dados');

  const newTicketsCount = draft.value.tasks.filter((t) => !t.existingTicketId).length;

  const handleSubmit = async () => {
    const groups = draft.validateDraft();
    if (groups.length > 0) {
      const firstGroup = groups[0]!;
      toast.error(firstGroup.errors[0] ?? 'Revise os dados do projeto.');
      setActiveTab(firstGroup.tab);
      return;
    }
    try {
      const created = await createMutation.mutateAsync(draft.toCreateRequest());
      navigate(`/helpdesk/projects/${created.id}`);
    } catch {
      // O hook useCreateProject já exibe o toast de erro.
    }
  };

  const tabs: TabItem[] = [
    { id: 'dados', label: 'Dados', icon: 'info', content: <DadosTab draft={draft} /> },
    {
      id: 'cronograma',
      label: 'Cronograma',
      icon: 'calendar',
      ...(draft.value.tasks.length > 0 ? { badge: draft.value.tasks.length } : {}),
      content: <CronogramaTab draft={draft} />,
    },
    {
      id: 'contatos',
      label: (
        <>
          <span className="sm:hidden">Contatos</span>
          <span className="hidden sm:inline">Contatos & Notificações</span>
        </>
      ),
      icon: 'bell',
      content: <ContatosNotificacoesTab draft={draft} />,
    },
  ];

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="project-form-page">
      <PageToolbar
        data-testid="project-form-toolbar"
        left={
          /* F25-F31: Breadcrumb shared (segmentos clicáveis + mobileLegend, nunca seta-voltar) */
          <Breadcrumb
            items={[
              { label: 'Help Desk', onNavigate: () => navigate('/helpdesk/tickets'), testid: 'project-form-breadcrumb-helpdesk' },
              { label: 'Projetos', onNavigate: () => navigate('/helpdesk/projects'), testid: 'project-form-breadcrumb-projects' },
              { label: 'Novo Projeto', testid: 'project-form-breadcrumb-current' },
            ]}
            mobileLegend="Help Desk · Projetos"
            data-testid="project-form-breadcrumb"
          />
        }
        right={
          <>
            <span className="text-sm hidden md:inline" style={{ color: 'var(--theme-muted-text)' }} data-testid="project-form-credits">
              {newTicketsCount > 0
                ? `Criará ${newTicketsCount} ticket${newTicketsCount > 1 ? 's' : ''} (${newTicketsCount} crédito${newTicketsCount > 1 ? 's' : ''})`
                : 'Adicione tarefas no Cronograma'}
            </span>
            <Button
              variant="secondary"
              className="hidden sm:inline-flex"
              onClick={() => navigate('/helpdesk/projects')}
              data-testid="project-form-cancel"
            >
              Cancelar
            </Button>
            {/* F01-F03: CTA primário — rótulo resiste (ds-lbl + cadeia @container) */}
            <Button
              icon="check"
              collapsibleLabel
              loading={createMutation.isPending}
              onClick={handleSubmit}
              data-testid="project-form-submit"
            >
              Criar Projeto
            </Button>
          </>
        }
      />

      <Tabs
        variant="underline"
        pageLevel
        fullBleed
        activeTab={activeTab}
        onChange={(t) => setActiveTab(t as ProjectFormTab)}
        tabs={tabs}
      />
    </div>
  );
};

ProjectFormPage.displayName = 'ProjectFormPage';
